import { createAppRequest } from '../request';
import type {
  ApiResponse,
  BpAnalysisItem,
  BusinessPlanItem,
  PageResult,
} from './startup.api';

const appRequest = createAppRequest();

export interface BusinessPlanListItem extends BusinessPlanItem {
  projectName?: string;
}

export interface FileUrlResult {
  url: string;
  urlMode: 'signed' | 'public_direct';
  expiresIn?: number | null;
}

export async function getBusinessPlans(params?: {
  projectUuid?: string;
  keyword?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ApiResponse<PageResult<BusinessPlanListItem>>> {
  return appRequest('/bff/business-plans', {
    method: 'GET',
    params,
  });
}

export async function getProjectBusinessPlans(
  projectUuid: string,
  params?: {
    pageNumber?: number;
    pageSize?: number;
  },
): Promise<ApiResponse<PageResult<BusinessPlanListItem>>> {
  return appRequest(`/bff/startup-projects/${projectUuid}/business-plans`, {
    method: 'GET',
    params,
  });
}

export async function getBusinessPlanDetail(
  uuid: string,
): Promise<ApiResponse<BusinessPlanListItem>> {
  return appRequest(`/bff/business-plans/${uuid}`, {
    method: 'GET',
  });
}

export async function uploadBusinessPlan(
  projectUuid: string,
  file: File,
  name?: string,
): Promise<ApiResponse<BusinessPlanListItem>> {
  const formData = new FormData();
  formData.append('file', file);
  if (name?.trim()) {
    formData.append('name', name.trim());
  }
  return appRequest(`/bff/startup-projects/${projectUuid}/business-plans`, {
    method: 'POST',
    data: formData,
  });
}

export async function getBusinessPlanPreviewUrl(
  uuid: string,
): Promise<ApiResponse<FileUrlResult>> {
  return appRequest(`/bff/business-plans/${uuid}/preview`, {
    method: 'GET',
  });
}

export async function getBusinessPlanDownloadUrl(
  uuid: string,
): Promise<ApiResponse<FileUrlResult>> {
  return appRequest(`/bff/business-plans/${uuid}/download`, {
    method: 'GET',
  });
}

export async function deleteBusinessPlan(uuid: string): Promise<ApiResponse<boolean>> {
  return appRequest(`/bff/business-plans/${uuid}`, {
    method: 'DELETE',
  });
}

export interface AnalyzeBusinessPlanResult {
  parseStatus: string;
  analysis: BpAnalysisItem | null;
  errorMessage?: string | null;
}

export async function analyzeBusinessPlan(
  uuid: string,
  forceParse = false,
): Promise<ApiResponse<AnalyzeBusinessPlanResult>> {
  return appRequest(`/bff/business-plans/${uuid}/analyze`, {
    method: 'POST',
    data: { forceParse },
    timeout: 180000,
  });
}

export async function getBusinessPlanAnalyses(
  uuid: string,
  params?: { pageNumber?: number; pageSize?: number },
): Promise<ApiResponse<PageResult<BpAnalysisItem>>> {
  return appRequest(`/bff/business-plans/${uuid}/analyses`, {
    method: 'GET',
    params,
  });
}

export async function getLatestBusinessPlanAnalysis(
  uuid: string,
): Promise<ApiResponse<BpAnalysisItem | null>> {
  return appRequest(`/bff/business-plans/${uuid}/analyses/latest`, {
    method: 'GET',
  });
}
